/**
 * Skat bot.
 *
 * Works from the bot's own view only:
 *  - Bidding: rates the ten cards for every suit game and Grand (jacks, trump length, aces) and for Null (how few high
 *    cards it holds). It bids up to the value of the best playable game, using the difficulty's aggression to set how
 *    strong "playable" has to be, and holds or passes against a bid accordingly.
 *  - Declaring: after seeing the skat it names the game with the best rating whose value still covers the bid, and
 *    discards the cards it needs least (the shortest side suits, or the highest cards for Null).
 *  - Play: the declarer draws trumps with its master trumps and cashes side aces; defenders lead aces, feed points to
 *    a partner who is winning and win as cheaply as possible otherwise. In Null the declarer ducks with the highest
 *    card that still loses and the defenders just keep low. Cards played so far are remembered (by difficulty).
 */
import { buildDeck, parseCard, type CardId } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import {
  skatCardPoints,
  skatEffectiveSuit,
  skatGameValue,
  skatIsTrump,
  skatMatadors,
  skatPower,
  skatTrickWinner,
  SKAT_BID_LADDER,
  SKAT_GAMES,
  SKAT_NULL_VALUE,
  type SkatAction,
  type SkatGame,
  type SkatLegal,
  type SkatTrickPlay,
  type SkatView,
} from './engine.js';

const rankOf = (id: CardId) => parseCard(id).rank;
const suitOf = (id: CardId) => parseCard(id).suit;

const NULL_RISK: Record<number, number> = { 14: 3, 13: 2.4, 12: 1.8, 11: 1.4, 10: 0.9, 9: 0.4, 8: 0, 7: 0 };
const TRUMP_SUIT_VALUE: Record<number, number> = { 14: 2.5, 10: 2, 13: 1.2, 12: 1, 9: 0.8, 8: 0.8, 7: 0.8 };
const nullRisk = (cards: readonly CardId[]): number => cards.reduce((n, c) => n + (NULL_RISK[rankOf(c)] ?? 0), 0);

/** How good the cards are for a suit or Grand game (higher = better). */
export function skatPlayScore(cards: readonly CardId[], game: SkatGame): number {
  if (game === 'NULL') return 0;
  const jacks = cards.filter((c) => rankOf(c) === 11).length;
  const side = cards.filter((c) => !skatIsTrump(c, game));
  const aces = side.filter((c) => rankOf(c) === 14).length;
  const protectedTens = side.filter((c) => rankOf(c) === 10 && cards.includes(`A${suitOf(c)}`)).length;
  if (game === 'GRAND') {
    const kings = side.filter((c) => rankOf(c) === 13 && cards.includes(`A${suitOf(c)}`)).length;
    return jacks * 4.9 + aces * 3 + protectedTens * 1.4 + kings * 0.6;
  }
  let trumpSuit = 0;
  for (const c of cards) {
    if (rankOf(c) === 11 || suitOf(c) !== game) continue;
    trumpSuit += TRUMP_SUIT_VALUE[rankOf(c)] ?? 0;
  }
  const short = (['S', 'H', 'D', 'C'] as const).filter((s) => s !== game && cards.filter((c) => suitOf(c) === s && rankOf(c) !== 11).length <= 1).length;
  return jacks * 3.5 + trumpSuit + aces * 2.2 + protectedTens * 1.2 + short * 0.5;
}

const playThreshold = (ctx: BotContext) => 13 - (ctx.profile.aggression - 0.5) * 6;
const nullThreshold = (ctx: BotContext) => 7.5 + (ctx.profile.aggression - 0.5) * 3;

/** The highest bid the bot is happy to make with these ten cards (0 = would not play at all). */
export function skatMaxBid(cards: readonly CardId[], ctx: BotContext): number {
  let best = 0;
  const need = playThreshold(ctx);
  for (const game of SKAT_GAMES) {
    if (game === 'NULL') continue;
    if (skatPlayScore(cards, game) < need) continue;
    const value = skatGameValue(game, skatMatadors(cards, game).count);
    best = Math.max(best, [...SKAT_BID_LADDER].reverse().find((v) => v <= value) ?? 0);
  }
  if (nullRisk(cards) <= nullThreshold(ctx)) best = Math.max(best, SKAT_NULL_VALUE);
  return best;
}

function bidChoice(view: SkatView, legal: Extract<SkatLegal, { kind: 'bid' | 'respond' }>, ctx: BotContext): SkatAction {
  const max = skatMaxBid(view.hand, ctx);
  if (legal.kind === 'respond') return max >= legal.current ? { type: 'hold' } : { type: 'pass' };
  const next = legal.values[0];
  return next !== undefined && next <= max ? { type: 'bid', value: next } : { type: 'pass' };
}

/** Which two cards the declarer can put away for `game` (the ones it needs least). */
export function skatChooseDiscard(hand: readonly CardId[], game: SkatGame): [CardId, CardId] {
  const len = (c: CardId) => hand.filter((h) => suitOf(h) === suitOf(c) && rankOf(h) !== 11).length;
  const keep = (c: CardId): number => {
    if (game === 'NULL') return -(NULL_RISK[rankOf(c)] ?? 0) * 10 - rankOf(c) * 0.1;
    if (skatIsTrump(c, game)) return 1000 + skatPower(c, game);
    if (rankOf(c) === 14) return 500;
    return len(c) * 10 + skatPower(c, game) - skatCardPoints(c) * 0.5;
  };
  const sorted = [...hand].sort((a, b) => keep(a) - keep(b));
  return [sorted[0]!, sorted[1]!];
}

function declareChoice(legal: Extract<SkatLegal, { kind: 'declare' }>, ctx: BotContext): SkatAction {
  const hand = legal.hand;
  const bid = legal.bid;
  const need = playThreshold(ctx);
  type Option = { game: SkatGame; rating: number; value: number };
  const options: Option[] = SKAT_GAMES.map((game) => {
    if (game === 'NULL') {
      const [a, b] = skatChooseDiscard(hand, 'NULL');
      const left = hand.filter((c) => c !== a && c !== b);
      return { game, rating: need + (nullThreshold(ctx) - nullRisk(left)) * 2, value: SKAT_NULL_VALUE };
    }
    return { game, rating: skatPlayScore(hand, game), value: skatGameValue(game, skatMatadors(hand, game).count) };
  });
  const covers = options.filter((o) => o.value >= bid);
  const pool = covers.length > 0 ? covers : options;
  const best = [...pool].sort((a, b) => (covers.length > 0 ? b.rating - a.rating : b.value - a.value))[0]!;
  return { type: 'declare', game: best.game, discard: skatChooseDiscard(hand, best.game) };
}

interface Knowledge {
  unseen: Set<CardId>;
}

function buildKnowledge(view: SkatView, ctx: BotContext): Knowledge {
  const seen = new Set<CardId>(view.hand);
  view.discard.forEach((c) => seen.add(c));
  view.trick.forEach((p) => seen.add(p.card));
  const remembered = ctx.profile.memory;
  view.tricks.forEach((t, i) => {
    // Forgetful bots only recall the most recent tricks.
    if (remembered >= 1 || i >= view.tricks.length - Math.max(1, Math.round(view.tricks.length * remembered))) {
      for (const p of t.plays) seen.add(p.card);
    }
  });
  return { unseen: new Set(buildDeck().filter((c) => rankOf(c) >= 7 && !seen.has(c))) };
}

function isMaster(card: CardId, k: Knowledge, game: SkatGame): boolean {
  const s = skatEffectiveSuit(card, game);
  const mine = skatPower(card, game);
  for (const u of k.unseen) if (skatEffectiveSuit(u, game) === s && skatPower(u, game) > mine) return false;
  return true;
}

const cost = (c: CardId, game: SkatGame) => skatCardPoints(c) * 10 + skatPower(c, game) + (skatIsTrump(c, game) ? 100 : 0);
const cheapest = (cards: readonly CardId[], game: SkatGame) => [...cards].sort((a, b) => cost(a, game) - cost(b, game))[0]!;
const richest = (cards: readonly CardId[], game: SkatGame) =>
  [...cards].sort((a, b) => skatCardPoints(b) - skatCardPoints(a) || skatPower(a, game) - skatPower(b, game))[0]!;

function wouldWin(trick: readonly SkatTrickPlay[], seat: number, card: CardId, game: SkatGame): boolean {
  if (trick.length === 0) return true;
  return skatTrickWinner([...trick, { seat, card }], game) === seat;
}

function lead(view: SkatView, legal: CardId[], k: Knowledge | null, game: SkatGame): CardId {
  const me = view.seat!;
  const isDeclarer = view.declarer === me;
  const trumps = legal.filter((c) => skatIsTrump(c, game));
  const side = legal.filter((c) => !skatIsTrump(c, game));
  if (isDeclarer && trumps.length > 0) {
    const top = [...trumps].sort((a, b) => skatPower(b, game) - skatPower(a, game))[0]!;
    const opponentsHaveTrumps = k ? [...k.unseen].some((u) => skatIsTrump(u, game)) : true;
    if (opponentsHaveTrumps && (k ? isMaster(top, k, game) : rankOf(top) === 11)) return top;
  }
  if (k) {
    const masters = side.filter((c) => isMaster(c, k, game));
    if (masters.length) return richest(masters, game);
  } else {
    const aces = side.filter((c) => rankOf(c) === 14);
    if (aces.length) return aces[0]!;
  }
  if (side.length) return cheapest(side, game);
  return cheapest(trumps.length ? trumps : legal, game);
}

function follow(view: SkatView, legal: CardId[], k: Knowledge | null, game: SkatGame): CardId {
  const me = view.seat!;
  const trick = view.trick;
  const winnerSeat = skatTrickWinner(trick, game);
  const winnerCard = trick.find((p) => p.seat === winnerSeat)!.card;
  const last = trick.length === 2;
  const partnerWinning = view.declarer !== me && winnerSeat !== view.declarer;
  const winners = legal.filter((c) => wouldWin(trick, me, c, game));

  if (partnerWinning) {
    const safe = last || (k ? isMaster(winnerCard, k, game) : rankOf(winnerCard) === 14 || skatIsTrump(winnerCard, game));
    return safe ? richest(legal, game) : cheapest(legal, game);
  }
  if (winners.length > 0) {
    const onTable = trick.reduce((n, p) => n + skatCardPoints(p.card), 0);
    // Do not spend a trump on an empty trick unless it is the last card.
    const plain = winners.filter((c) => !skatIsTrump(c, game) || last || onTable >= 10);
    return cheapest(plain.length ? plain : winners, game);
  }
  return cheapest(legal, game);
}

/** Null: the declarer ducks under the current winner; defenders simply stay low. */
function nullPlay(view: SkatView, legal: CardId[]): CardId {
  const power = (c: CardId) => skatPower(c, 'NULL');
  const byPower = [...legal].sort((a, b) => power(a) - power(b));
  if (view.declarer !== view.seat) return byPower[0]!;
  if (view.trick.length === 0) return byPower[0]!;
  const leadSuit = suitOf(view.trick[0]!.card);
  const best = Math.max(...view.trick.filter((p) => suitOf(p.card) === leadSuit).map((p) => power(p.card)));
  const following = legal.some((c) => suitOf(c) === leadSuit);
  if (!following) return byPower[byPower.length - 1]!;
  const ducks = byPower.filter((c) => power(c) < best);
  return ducks.length > 0 ? ducks[ducks.length - 1]! : byPower[0]!;
}

export function skatBotChoose(view: SkatView, legal: CardId[], ctx: BotContext): CardId {
  if (legal.length === 1) return legal[0]!;
  const game = view.game!;
  if (game === 'NULL') return nullPlay(view, legal);
  const k = ctx.profile.memory > 0 ? buildKnowledge(view, ctx) : null;
  return view.trick.length === 0 ? lead(view, legal, k, game) : follow(view, legal, k, game);
}

export const skatBot: BotEngine<SkatView, SkatLegal, SkatAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'bid' || legal.kind === 'respond') return this.chooseBet!(view, legal, ctx);
    if (legal.kind === 'pickup') return { type: 'pickup' };
    if (legal.kind === 'declare') return declareChoice(legal, ctx);
    if (legal.kind === 'play') return this.chooseCard!(view, legal, ctx);
    throw new Error('Bot asked to act without legal actions');
  },

  chooseBet(view, legal, ctx) {
    if (legal.kind !== 'bid' && legal.kind !== 'respond') return this.handleTimeout(view, legal, ctx);
    if (chance(ctx, ctx.profile.mistakeRate)) {
      if (legal.kind === 'respond') return ctx.rng.next() < 0.5 ? { type: 'hold' } : { type: 'pass' };
      const first = legal.values[0];
      return first !== undefined && ctx.rng.next() < 0.5 ? { type: 'bid', value: first } : { type: 'pass' };
    }
    return bidChoice(view, legal, ctx);
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    const cards = legal.cards;
    if (cards.length === 1) return { type: 'play', card: cards[0]! };
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'play', card: cards[ctx.rng.int(cards.length)]! };
    const card = skatBotChoose(view, cards, ctx);
    return { type: 'play', card: cards.includes(card) ? card : cards[0]! };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const others = view.scores.filter((_, s) => s !== me);
    const diff = view.scores[me]! - Math.max(...others);
    return Math.max(0, Math.min(1, 0.5 + diff / 200));
  },

  calculateRisk(_view, legal) {
    return legal.kind === 'bid' && legal.values.length > 0 ? 0.3 : 0;
  },

  handleTimeout(view, legal) {
    if (legal.kind === 'bid' || legal.kind === 'respond') return { type: 'pass' };
    if (legal.kind === 'pickup') return { type: 'pickup' };
    if (legal.kind === 'declare') return { type: 'declare', game: 'C', discard: skatChooseDiscard(legal.hand, 'C') };
    if (legal.kind === 'play') return { type: 'play', card: cheapest(legal.cards, view.game ?? 'C') };
    throw new Error('No legal action');
  },
};

